/**
 * decorators.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

// import "reflect-metadata";
import { ProtocolVersionError } from "./errors";

declare const window : Window & {protocolVersion : number};

const PERMISSIVE_DESCRIPTOR : PropertyDescriptor = {
  writable: true,
  configurable: true,
  enumerable: true
};

export function descriptor (options : PropertyDescriptor) : PropertyDecorator
{
  return (target : any, propertyKey : string) => {
    const descriptor : PropertyDescriptor = Object.assign(
      Object.getOwnPropertyDescriptor(target, propertyKey) || {},
      options
    );
    Object.defineProperty(target, propertyKey, descriptor);
  };
}

type VersionRequirement = number | ((version : number) => boolean);

/**
 * Replace the decorated property with a getter-setter pair, whose getter will
 * inject the fallback value if the protocol version does not satisfy the
 * version requirement.
 * @param req number|Function Either the minimum supported version or a
 *                            predicate identifying supported versions.
 * @param fallback any        A value to return when the version is unsatisfied.
 */
export function protocolDefault (req : VersionRequirement, fallback : any)
  : PropertyDecorator
{
  const satisfiesReq : ((version : number) => boolean) = (
    (typeof req === "number")
      ? (version : number) => (version >= req)
      : req
  );
  return (target : any, propertyKey : string) => {
    let value : any;
    return {
      get: () => (!satisfiesReq(window.protocolVersion)) ? fallback : value,
      set: (newValue : any) => value = newValue
    };
  };
}

/**
 * Replace the decorated property with a getter that throws an error if the
 * protocol version is not at least the version specified.
 * @param minimum number  Minimum version required to access this property
 */
export function protocolMinimum (minimum : number)
  : PropertyDecorator
{
  return (target : any, propertyKey : string) => {
    let value : any;
    return {
      get: () : undefined => {
        // TODO: we're checking against a global flag (UGH!) because we don't
        // yet have the machinery to inject the version into every msg class
        if (window.protocolVersion < minimum)
        {
          throw new ProtocolVersionError(minimum, window.protocolVersion);
        }
        else
        {
          return value;
        }
      },
      set: (newValue : any) => value = newValue
    };
  };
}

/**
 * Prepare a target class to be able to handle decorated actions on disconnect.
 * @param target The class to prepare for disconnect features
 */
function initDisconnector (target : any) : void
{
  if (typeof target.disconnected !== "function")
  {
    console.error(target);
    throw new Error("disconnect() is not defined on target");
  }
  if (!target.disconnected.wrapped)
  {
    const oldFn : () => void = target.disconnected;
    const map : Map<string, (current : any) => any> = new Map();
    // tslint:disable-next-line:only-arrow-functions
    target.disconnected = function () : void
    {
      map.forEach((fn : (current : any) => any, property : string) =>
        this[property] = fn.call(this, this[property]));
      oldFn.call(this);
    };
    target.disconnected.add =
      (property : string, fn : (current : any) => any) => map.set(property, fn);
    target.disconnected.wrapped = true;
  }
}

/**
 * When the disconnect function is run, also run the provided callback.
 * @param fn The callback to execute, with the decorated property as parameter
 */
export function disconnectDo (fn : (current : any) => any) : PropertyDecorator
{
  return (target : any, key : string) => {
    initDisconnector(target);
    target.disconnected.add(key, fn);
  };
}

/**
 * When the disconnect function is run, run a function on the decorated object.
 * @param method The string name of the method to run
 * @param arg A single argument to pass to the method
 */
export function disconnectWith (method : string, arg? : any)
  : PropertyDecorator
{
  return disconnectDo((obj : any) => {
    obj[method](arg);
    return obj;
  });
}

/**
 * When the disconnect function is run, set the decorated property to this value
 * @param value The value to set the decorated field to
 */
export function disconnectValue (value : any) : PropertyDecorator
{
  return disconnectDo(() : any => value);
}

/**
 * When the disconnect function is run, set the decoratee to an empty array
 */
export function disconnectEmpty () : PropertyDecorator
{
  return disconnectDo(() : any[] => []);
}

/**
 * When the disconnect function is run, run the decorated method as well
 */
export function onDisconnect () : MethodDecorator
{
  return (target : any, method : string, descriptor : PropertyDescriptor) => {
    initDisconnector(target);
    target.disconnected.add(Symbol(method), descriptor.value);
  };

}

// export function demarshalTimestamp () : PropertyDecorator
// {
//   return (proto : any, key : string) => {
//     proto[Symbol.for("demarshal_list")].push(
//       [key, (input : number) => new Date(input)]
//     );
//   };
// }



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/types/decorators.ts